import { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, MapPin, Search, X, Save } from 'lucide-react';
import { api } from '@/lib/api';
import type { GeocodingResult, Place, PlaceWithCounts } from '@antree/shared';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in Leaflet with webpack/vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface GeocodingResponse {
  results: GeocodingResult[];
}

interface PlacesResponse {
  places: PlaceWithCounts[];
}

interface PlaceSearchProps {
  projectId: string;
  value: Place | null;
  onChange: (place: Place | null) => void;
  placeholder?: string;
}

// Component to center map only once when opened
function MapUpdater({ position, placeId }: { position: [number, number] | null; placeId: string | undefined }) {
  const map = useMap();
  const lastPlaceIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Only update view when a different place is selected
    if (position && placeId !== lastPlaceIdRef.current) {
      map.setView(position, 17);
      lastPlaceIdRef.current = placeId;
    }
  }, [map, position, placeId]);

  return null;
}

// Draggable marker component
interface DraggableMarkerProps {
  position: [number, number];
  onPositionChange: (lat: number, lng: number) => void;
}

function DraggableMarker({ position, onPositionChange }: DraggableMarkerProps) {
  const markerRef = useRef<L.Marker>(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker) {
          const latlng = marker.getLatLng();
          onPositionChange(latlng.lat, latlng.lng);
        }
      },
    }),
    [onPositionChange]
  );

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    />
  );
}

export function PlaceSearch({ projectId, value, onChange, placeholder = 'Ort suchen...' }: PlaceSearchProps) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<GeocodingResult[]>([]);
  const [existingPlaces, setExistingPlaces] = useState<PlaceWithCounts[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [draggedPosition, setDraggedPosition] = useState<[number, number] | null>(null);
  const [savingPosition, setSavingPosition] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load existing places for quick selection
  useEffect(() => {
    async function loadPlaces() {
      const response = await api.get<PlacesResponse>(`/projects/${projectId}/places`);
      if (response.success && response.data) {
        setExistingPlaces(response.data.places);
      }
    }
    loadPlaces();
  }, [projectId]);

  // Close results when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (query.length < 3) {
      setResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      const response = await api.get<GeocodingResponse>(`/projects/${projectId}/places/geocode/search?q=${encodeURIComponent(query)}`);
      if (response.success && response.data) {
        setResults(response.data.results);
      }
      setSearching(false);
    }, 300);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [query, projectId]);

  // Filter existing places by query
  const filteredExisting = existingPlaces.filter(
    (p) =>
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.city?.toLowerCase().includes(query.toLowerCase()) ||
      p.country?.toLowerCase().includes(query.toLowerCase())
  );

  async function selectGeocodingResult(result: GeocodingResult) {
    // Create a new place from the geocoding result
    const response = await api.post<{ place: Place }>(`/projects/${projectId}/places`, {
      name: result.address.city || result.displayName.split(',')[0],
      street: result.address.street,
      houseNumber: result.address.houseNumber,
      postalCode: result.address.postalCode,
      city: result.address.city,
      region: result.address.region,
      country: result.address.country,
      latitude: result.latitude,
      longitude: result.longitude,
    });

    if (response.success && response.data) {
      onChange(response.data.place);
      setExistingPlaces([...existingPlaces, response.data.place as PlaceWithCounts]);
    }
    setQuery('');
    setShowResults(false);
  }

  function selectExistingPlace(place: PlaceWithCounts) {
    onChange(place);
    setQuery('');
    setShowResults(false);
  }

  function confirmClearSelection() {
    onChange(null);
    setDraggedPosition(null);
    setShowDeleteConfirm(false);
  }

  function handleMarkerDrag(lat: number, lng: number) {
    setDraggedPosition([lat, lng]);
  }

  async function saveNewPosition() {
    if (!value || !draggedPosition) return;

    setSavingPosition(true);

    // Check if place is used elsewhere - if so, create a copy
    const placeUsage = existingPlaces.find(p => p.id === value.id);
    const usageCount = placeUsage?._count
      ? (placeUsage._count.births || 0) + (placeUsage._count.deaths || 0) + (placeUsage._count.burials || 0) + (placeUsage._count.events || 0)
      : 0;

    if (usageCount > 1) {
      // Create a new place with the adjusted coordinates
      const response = await api.post<{ place: Place }>(`/projects/${projectId}/places`, {
        name: value.name,
        street: value.street,
        houseNumber: value.houseNumber,
        postalCode: value.postalCode,
        city: value.city,
        region: value.region,
        country: value.country,
        latitude: draggedPosition[0],
        longitude: draggedPosition[1],
        notes: value.notes,
      });

      if (response.success && response.data) {
        onChange(response.data.place);
        setDraggedPosition(null);
        setExistingPlaces(prev => [...prev, response.data!.place as PlaceWithCounts]);
      }
    } else {
      // Update existing place
      const response = await api.put<{ place: Place }>(`/projects/${projectId}/places/${value.id}`, {
        latitude: draggedPosition[0],
        longitude: draggedPosition[1],
      });

      if (response.success && response.data) {
        onChange(response.data.place);
        setDraggedPosition(null);
        setExistingPlaces(prev =>
          prev.map(p => p.id === value.id ? { ...p, ...response.data!.place } as PlaceWithCounts : p)
        );
      }
    }
    setSavingPosition(false);
  }

  const mapPosition: [number, number] | null = value?.latitude && value?.longitude
    ? [value.latitude, value.longitude]
    : null;

  const currentMarkerPosition = draggedPosition || mapPosition;
  const hasPositionChanged = draggedPosition !== null;

  return (
    <div ref={containerRef} className="relative">
      {value ? (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
          <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{value.name}</p>
            {(value.city || value.country) && (
              <p className="text-sm text-muted-foreground truncate">
                {[value.city, value.region, value.country].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {value.latitude && value.longitude && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowMap(!showMap)}
              >
                <MapPin className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
            placeholder={placeholder}
            className="pl-9"
          />
          {searching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      )}

      {/* Results dropdown */}
      {showResults && !value && (query.length >= 1 || existingPlaces.length > 0) && (
        <Card className="absolute z-50 w-full mt-1 max-h-64 overflow-auto">
          {/* Existing places */}
          {filteredExisting.length > 0 && (
            <div className="p-2">
              <p className="text-xs font-medium text-muted-foreground mb-1 px-2">Vorhandene Orte</p>
              {filteredExisting.slice(0, 5).map((place) => (
                <button
                  key={place.id}
                  type="button"
                  className="w-full text-left px-2 py-1.5 hover:bg-muted rounded-sm"
                  onClick={() => selectExistingPlace(place)}
                >
                  <p className="font-medium">{place.name}</p>
                  {(place.city || place.country) && (
                    <p className="text-sm text-muted-foreground">
                      {[place.city, place.country].filter(Boolean).join(', ')}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Geocoding results */}
          {results.length > 0 && (
            <div className="p-2 border-t">
              <p className="text-xs font-medium text-muted-foreground mb-1 px-2">OpenStreetMap</p>
              {results.map((result, index) => (
                <button
                  key={index}
                  type="button"
                  className="w-full text-left px-2 py-1.5 hover:bg-muted rounded-sm"
                  onClick={() => selectGeocodingResult(result)}
                >
                  <p className="text-sm">{result.displayName}</p>
                </button>
              ))}
            </div>
          )}

          {/* No results */}
          {query.length >= 3 && !searching && results.length === 0 && filteredExisting.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Keine Orte gefunden
            </div>
          )}

          {/* Hint */}
          {query.length < 3 && filteredExisting.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Mindestens 3 Zeichen eingeben
            </div>
          )}
        </Card>
      )}

      {/* Map preview */}
      {showMap && mapPosition && currentMarkerPosition && (
        <Card className="mt-2 p-2">
          <div className="h-48 rounded-md overflow-hidden">
            <MapContainer
              center={mapPosition}
              zoom={15}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <DraggableMarker
                position={currentMarkerPosition}
                onPositionChange={handleMarkerDrag}
              />
              <MapUpdater position={mapPosition} placeId={value?.id} />
            </MapContainer>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Marker verschieben für genaue Position
          </p>
          {hasPositionChanged && (
            <div className="flex items-center justify-between mt-2 pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                Neue Position: {draggedPosition[0].toFixed(6)}, {draggedPosition[1].toFixed(6)}
              </p>
              <Button
                type="button"
                size="sm"
                onClick={saveNewPosition}
                disabled={savingPosition}
              >
                {savingPosition ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Speichern
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Bestätigungsdialog zum Entfernen */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ort entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie "{value?.name}" wirklich entfernen? Der Ort bleibt im Projekt erhalten und kann erneut ausgewählt werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmClearSelection}>
              Entfernen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
